// src/pages/AppliAdmin/ContactForm/components/ContactMessageTable.jsx
import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaCopy,
  FaTrash,
} from "react-icons/fa";
import {
  cleanMessage,
  formatDateTimeFR,
  formatRelativeDate,
} from "../contactFormUtils";
import { ReadToggle } from "./ContactMessageCard";

export default function ContactMessageTable({
  rows,
  deletingId,
  markingId,
  onDelete,
  onToggleRead,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const copyEmail = async (event, email) => {
    event.preventDefault();
    event.stopPropagation();

    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
    } catch {
      console.warn("Impossible de copier l'email");
    }
  };

  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
          <tr>
            <th className="px-5 py-4">Contact</th>
            <th className="px-5 py-4">Message</th>
            <th className="px-5 py-4">Date</th>
            <th className="px-5 py-4">Statut</th>
            <th className="px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((message) => {
            const isRead = Boolean(message.read_message);
            const isExpanded = expandedIds.has(message.id);
            const messageText = cleanMessage(message.message);
            const shouldShowToggle = messageText.length > 220;

            return (
              <tr
                key={message.id}
                className={`transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50 ${
                  !isRead ? "bg-indigo-50/40 dark:bg-indigo-950/20" : ""
                }`}
              >
                <td className="px-5 py-4 align-top">
                  <div className="font-bold text-slate-900 dark:text-white">
                    {message.name || "Nom non renseigné"}
                  </div>

                  <div className="mt-2 flex flex-col gap-2">
                    <span className="break-all text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {message.email || "Email non renseigné"}
                    </span>

                    {message.email ? (
                      <button
                        type="button"
                        onClick={(event) => copyEmail(event, message.email)}
                        className="inline-flex w-fit items-center gap-1 rounded-xl bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <FaCopy />
                        Copier email
                      </button>
                    ) : null}
                  </div>
                </td>

                <td className="max-w-xl px-5 py-4 align-top">
                  <div
                    className={`rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60 ${
                      isExpanded ? "" : "max-h-28 overflow-hidden"
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                      {messageText || "—"}
                    </p>
                  </div>

                  {shouldShowToggle ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleExpanded(message.id);
                      }}
                      className="mt-2 inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      {isExpanded ? (
                        <>
                          <FaChevronUp />
                          Réduire
                        </>
                      ) : (
                        <>
                          <FaChevronDown />
                          Voir le message complet
                        </>
                      )}
                    </button>
                  ) : null}
                </td>

                <td className="whitespace-nowrap px-5 py-4 align-top">
                  <div className="font-bold text-slate-800 dark:text-slate-100">
                    {formatRelativeDate(message.created_at)}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {formatDateTimeFR(message.created_at)}
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <ReadToggle
                    read={isRead}
                    disabled={markingId === message.id}
                    onToggle={() => onToggleRead(message)}
                  />
                </td>

                <td className="px-5 py-4 text-right align-top">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDelete(message.id);
                    }}
                    disabled={deletingId === message.id}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3.5 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}